import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useStyletron } from 'baseui'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import useAdditionalSaleFormData from '../AdditionalSales/useAdditionalSaleFormData'
import useCreationColumn from '../AdditionalSales/useCreationColumn'
import { formatPrice } from './formatPrice'
import { saleProgress, type ProgressSaleData, type ScriptFormula } from './saleProgress'
import { progressDefinitionOf } from './saleProgressRegistry'
import useScriptLocale from './scripts/blocks/useScriptLocale'
import type { SaleAuthData, SaleResource } from './types'

const useSaleStatusPanel = (saleType: string) => {
  const { retrieveResourcesOfSale, retrieveProductsListOfSale, retryAdditionalSaleLeadUrl, updateLead } =
    useAdditionalSaleFormData()
  const updateLeadDispatch = (payload: Record<string, unknown>) => updateLead(payload, saleType)
  const { retryLeadRequest, saleDataState, hasBeenRetried, isSaleInRetryList, creationDate, t } = useCreationColumn({
    saleType,
    updateLeadDispatch,
  })
  const [css, theme] = useStyletron()
  const { country }: SaleAuthData = useSelector(selectAuthData) ?? {}
  const data = (saleDataState ?? {}) as ProgressSaleData & { zohoContractId?: string }
  const products = (retrieveProductsListOfSale(saleType) ?? []) as ScriptFormula[]
  const isFrance = (country ?? '').toLowerCase() === 'fr'
  const definition = isFrance ? progressDefinitionOf(saleType) : undefined
  const progress = saleProgress(data, definition, { country: country ?? '' })
  const locale = useScriptLocale()

  const doneKeys = progress.steps.filter(step => step.state === 'done').map(step => step.key)
  const seenSteps = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (seenSteps.current === null) {
      seenSteps.current = new Set(doneKeys)
      return
    }
    const fresh = doneKeys.filter(key => !seenSteps.current?.has(key))
    seenSteps.current = new Set(doneKeys)
    if (!fresh.length) return
    const now = new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    updateLeadDispatch({
      stepTimes: { ...data.stepTimes, ...Object.fromEntries(fresh.map(key => [key, now])) },
    })
  }, [doneKeys.join()])

  const formulaId = definition?.formulaId?.(data)
  const formula = products.find(product => String(product.id) === String(formulaId))
  const rawPrice = Number(formula?.attributes?.price ?? 0)
  const price = formatPrice(formula?.attributes?.price, locale)
  const perDay = rawPrice
    ? t('additionalSales:pulse.per_day_cents', { cents: Math.round(((rawPrice * 12) / 365) * 100) })
    : ''
  const panelValues: Record<string, string> = { price, perDay, ...definition?.panelValues?.(formula, data) }
  const variableClass = css({ fontWeight: 700, color: theme.colors.primaryPulse })
  const highlight = (value: string) => `<b class="${variableClass}">${value}</b>`

  return {
    t,
    data,
    progress,
    WaitActions: progress.isWaiting ? definition?.waitActions : undefined,
    resources: (retrieveResourcesOfSale(saleType) ?? []) as SaleResource[],
    panelValues,
    highlightedValues: Object.fromEntries(Object.entries(panelValues).map(([key, value]) => [key, highlight(value)])),
    retry: {
      actionsInProgress: Boolean(data.actionsInProgress),
      hidden: !isSaleInRetryList || progress.tone === 'signed',
      hasBeenRetried: Boolean(hasBeenRetried),
      creationDate,
      updateLeadDispatch,
      callback: () => retryLeadRequest(retryAdditionalSaleLeadUrl),
    },
  }
}

export default useSaleStatusPanel
